import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Snackbar,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import LucideIcon from "../../../../components/LucideIcon";
import { Company } from "../../../../types";
import { hostname, parseError } from "../../../../utils";
import googleLogo from "../../../../img/google-logo.png";
import office365Logo from "../../../../img/office-365-logo.png";
import InstallIntegrationDialog from "../../../integrations/InstallIntegrationDialog";

// The three mailbox providers the user can connect during onboarding. These map to the
// `selectedIntegration` values InstallIntegrationDialog already understands, so connecting
// here runs the exact same OAuth + install flow as the Inbox screen.
type MailboxProvider = "gmail" | "office365" | "imap";

interface ProviderCard {
  provider: MailboxProvider;
  title: string;
  logo?: string; // image logo (Gmail / Office 365)
  icon?: string; // Lucide icon name (IMAP)
}

const PROVIDERS: ProviderCard[] = [
  {
    provider: "gmail",
    title: "Gmail",
    logo: googleLogo,
  },
  {
    provider: "office365",
    title: "Office 365",
    logo: office365Logo,
  },
  {
    provider: "imap",
    title: "IMAP Email",
    icon: "Mail",
  },
];

interface SequenceMailboxConnectProps {
  companyId: string;
  // Minimal company shell — InstallIntegrationDialog only reads `company.uid` (for Slack,
  // not used here). Onboarding's guided flow runs only on the main site, so the dialog's
  // whitelabel OAuth guard never trips.
  company: Company;
  getToken: () => Promise<string>;
  // The onboarding-generated sequence's id, so the backend can attach the new sender to it.
  // Optional: the backend falls back to the company's most recent sequence when omitted.
  sequenceId?: string;
}

interface EmailIntegration {
  id: string;
  name?: string;
  instanceName?: string;
  parameters?: { email?: string };
}

// The displayed email for an integration. The by-type/email API drops parameters.email for
// Composio-backed mailboxes (Gmail), but every email integration also stores the address in
// instanceName — so fall back to that.
function integrationEmail(i?: EmailIntegration | null): string {
  if (!i) return "";
  return i.parameters?.email || i.instanceName || i.name || "";
}

// Inline mailbox-connection section shown on the sequence step. Connecting a mailbox is what
// makes the generated sequence actually sendable: we install the email integration + inbox via
// the shared installer, then call the onboarding backend to create a sender profile wired to it
// and attach it to the generated sequence — so the sequence is ready to run when onboarding ends.
export default function SequenceMailboxConnect({
  companyId,
  company,
  getToken,
  sequenceId,
}: SequenceMailboxConnectProps) {
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState<EmailIntegration | null>(null);
  const [openProvider, setOpenProvider] = useState<MailboxProvider | null>(null);
  const [settingUp, setSettingUp] = useState(false);
  const [error, setError] = useState("");

  // The onboarding sequence is created inactive (it must not send before the user opts in). This
  // toggle is how the user turns sending on, and it only appears once a mailbox is connected.
  const [sendingEnabled, setSendingEnabled] = useState(false);
  const [togglingSending, setTogglingSending] = useState(false);

  // Pull the company's email integrations; the first one (if any) is treated as the connected
  // mailbox for onboarding purposes (most users connect exactly one here).
  const loadConnected = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(`${hostname}/integrations/by-type/email`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });
      const items: EmailIntegration[] = res.data?.items || [];
      setConnected(items.length ? items[0] : null);
      return items;
    } catch (err) {
      // A failed lookup shouldn't block the step — the user can still connect; we just won't
      // show an existing connection.
      console.error("[SequenceMailboxConnect] failed to load email integrations", err);
      return [] as EmailIntegration[];
    }
  }, [companyId, getToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadConnected();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadConnected]);

  // After the install dialog reports success, hand off to the onboarding backend to create a
  // sender profile bound to the new mailbox and attach it to the generated sequence. Doing this
  // server-side avoids the by-type API's email-clobber for Gmail and the destructive
  // full-replace of POST /sequences. Best-effort: if it fails the mailbox is still connected and
  // the user can finish setup from the Sequences page.
  const handleInstalled = useCallback(async () => {
    setOpenProvider(null);
    setSettingUp(true);
    setError("");
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/onboarding/connect-sender`,
        { companyId, sequenceId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh so the connected-mailbox state renders.
      await loadConnected();
    } catch (err) {
      console.error("[SequenceMailboxConnect] failed to wire sender to sequence", err);
      // The mailbox itself did connect (the installer succeeded before this call); reflect that.
      await loadConnected();
      setError(
        "Mailbox connected, but we couldn't finish sender setup automatically. You can complete it on the Sequences page."
      );
    } finally {
      setSettingUp(false);
    }
  }, [companyId, sequenceId, getToken, loadConnected]);

  // Turn sending on/off for the onboarding sequence. The backend gates activation on a
  // connected mailbox, so a "no_email_integration" response leaves the toggle off.
  const handleToggleSending = useCallback(
    async (next: boolean) => {
      setTogglingSending(true);
      setError("");
      // Optimistic flip; revert if the backend refuses or errors.
      setSendingEnabled(next);
      try {
        const token = await getToken();
        const res = await axios.post(
          `${hostname}/onboarding/sequence/activate`,
          { companyId, active: next, sequenceId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const applied = !!res.data?.active;
        setSendingEnabled(applied);
        if (next && !applied) {
          setError(
            "Connect a mailbox first — we need somewhere to send from before this sequence can go live."
          );
        }
      } catch (err) {
        console.error("[SequenceMailboxConnect] failed to toggle sending", err);
        setSendingEnabled(!next);
        setError(parseError(err, "Couldn't update sending. Please try again."));
      } finally {
        setTogglingSending(false);
      }
    },
    [companyId, sequenceId, getToken]
  );

  const sectionHeader = (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Connect your mailbox
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Connect the inbox you'll send from so this sequence can go live. We'll set up a sender
        for you automatically.
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ mt: 4 }}>
        {sectionHeader}
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      {sectionHeader}

      {connected ? (
        <Box>
          <Alert
            severity="success"
            icon={<LucideIcon name="MailCheck" size={22} color={theme.palette.primary.main} />}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {integrationEmail(connected) || "Mailbox connected"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {sendingEnabled
                ? "Sending is on — your sequence will start reaching out to your leads."
                : "Your sequence is ready. Turn on sending when you're ready to start reaching out."}
            </Typography>
          </Alert>

          {/* Sending stays OFF until the user opts in, even with a mailbox connected — nothing
              goes out without an explicit choice. */}
          <Box
            sx={{
              mt: 1.5,
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Enable sending
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Start sending this sequence to your enrolled leads. You can pause it anytime from
                the Sequences page.
              </Typography>
            </Box>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  checked={sendingEnabled}
                  disabled={togglingSending}
                  onChange={(e) => handleToggleSending(e.target.checked)}
                />
              }
              label=""
            />
          </Box>
        </Box>
      ) : settingUp ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Setting up your sender…
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 2,
          }}
        >
          {PROVIDERS.map((p) => (
            <Card
              key={p.provider}
              variant="outlined"
              onClick={() => setOpenProvider(p.provider)}
              sx={{
                cursor: "pointer",
                height: "100%",
                transition: "border-color 120ms, background-color 120ms",
                "&:hover": {
                  bgcolor: "action.hover",
                  borderColor: "primary.main",
                },
              }}
            >
              <CardContent sx={{ textAlign: "center", py: 2.5 }}>
                <Box sx={{ mb: 1.5, height: 40, display: "flex", justifyContent: "center", alignItems: "center" }}>
                  {p.logo ? (
                    <img src={p.logo} alt={p.title} style={{ width: 36, height: 36 }} />
                  ) : (
                    <LucideIcon name={p.icon || "Mail"} size={36} color={theme.palette.primary.main} />
                  )}
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {p.title}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Reuse the shared installer for each provider — it owns the OAuth popup / credential
          flow and creates the integration + inbox. We just react to its success callback. */}
      {(["gmail", "office365", "imap"] as MailboxProvider[]).map((provider) => (
        <InstallIntegrationDialog
          key={provider}
          open={openProvider === provider}
          onClose={() => setOpenProvider(null)}
          selectedIntegration={provider}
          company={company}
          companyId={companyId}
          getToken={getToken}
          eventListener={(event: any) => {
            if (event?.type === "SET_ERROR") {
              setError(event.error || "Something went wrong connecting your mailbox.");
            }
          }}
          onSaveSuccess={handleInstalled}
        />
      ))}

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="warning" onClose={() => setError("")} sx={{ width: "100%" }}>
          {typeof error === "string" ? error : parseError(error)}
        </Alert>
      </Snackbar>
    </Box>
  );
}
